import { useLocation, useNavigate } from "@tanstack/react-router";
import { Plus, Search } from "lucide-react";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";
import { CustomAvatar } from "@/components/common/CustomAvatar";
import { ListContainer, ListItem } from "@/components/common/ListItem";
import { Button } from "@/components/ui/button";
import { ChatSearchModal } from "@/features/chat/components/ChatSearchModal";
import { CreateChatModal } from "@/features/chat/components/CreateChatModal";
import { useChatRooms } from "@/hooks/useChatRooms";
import type { ChatRoomResponse } from "@/lib/humhub-api/types";

interface ChatListProps {
  containerHeight?: string | number;
  onClick?: () => void;
}

export interface ChatListRef {
  refetch: () => Promise<void>;
}

// ローディング状態コンポーネント
const LoadingState: React.FC<{ message: string }> = ({ message }) => (
  <div className="py-4 text-center text-variable-collection-dark-gray">{message}</div>
);

// エラー状態コンポーネント
const ErrorState: React.FC<{ error: string }> = ({ error }) => (
  <div className="py-4 text-center text-variable-collection-red">{error}</div>
);

// 空状態コンポーネント
const EmptyState: React.FC<{ message: string }> = ({ message }) => (
  <div className="py-4 text-center text-variable-collection-dark-gray">{message}</div>
);

// もっと見るボタンコンポーネント
const LoadMoreButton: React.FC<{
  onLoadMore: () => void;
  isLoading: boolean;
  loadingText: string;
  moreText: string;
}> = ({ onLoadMore, isLoading, loadingText, moreText }) => (
  <div className="p-2 text-center">
    <Button onClick={onLoadMore} disabled={isLoading} variant="teal">
      {isLoading ? loadingText : moreText}
    </Button>
  </div>
);

export const ChatList = forwardRef<ChatListRef, ChatListProps>(
  ({ containerHeight = "450px", onClick }, ref) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { pathname } = useLocation();

    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);

    const PER_PAGE = 50;
    const [currentPage, setCurrentPage] = useState(1);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [accumulatedRooms, setAccumulatedRooms] = useState<ChatRoomResponse[]>([]);

    const {
      data: chatRooms,
      pagination,
      isLoading,
      error,
      handlePageChange,
      refetch,
    } = useChatRooms(currentPage, PER_PAGE);

    // データをリフレッシュする関数
    const handleRefresh = useCallback(async () => {
      setCurrentPage(1);
      setAccumulatedRooms([]);

      if (currentPage !== 1) {
        return;
      }

      await refetch();
    }, [refetch, currentPage]);

    // chatRoomsが更新されたら累積データに追加
    useEffect(() => {
      if (chatRooms && chatRooms.length > 0) {
        if (currentPage === 1) {
          setAccumulatedRooms(chatRooms);
        } else {
          setAccumulatedRooms((prev) => {
            const existingIds = new Set(prev.map((room) => room.id));
            const newRooms = chatRooms.filter((room) => !existingIds.has(room.id));
            return [...prev, ...newRooms];
          });
        }
      }
    }, [chatRooms, currentPage]);

    const loadMore = async () => {
      if (isLoadingMore || !pagination) {
        return;
      }

      if (currentPage < pagination.pages) {
        setIsLoadingMore(true);

        const nextPage = currentPage + 1;
        setCurrentPage(nextPage);

        try {
          await handlePageChange(nextPage);
        } finally {
          setIsLoadingMore(false);
        }
      }
    };

    const hasMore = pagination ? currentPage < pagination.pages : false;

    const handleChatItemClick = (roomId: number | undefined) => {
      if (onClick) {
        onClick();
      }
      navigate({ to: `/chat-room/${roomId}` });
    };

    const getCurrentChatRoomId = () => {
      const match = pathname.match(/\/chat-room\/(\d+)/);
      return match ? match[1] : null;
    };

    const currentChatRoomId = getCurrentChatRoomId();

    // チャット作成時の処理 - 直接リフレッシュを呼び出す
    const handleChatCreated = useCallback(async () => {
      await handleRefresh();
    }, [handleRefresh]);

    // 外部からrefetchを呼び出せるようにする
    useImperativeHandle(
      ref,
      () => ({
        refetch: handleRefresh,
      }),
      [handleRefresh],
    );

    const renderContent = () => {
      if (isLoading && accumulatedRooms.length === 0) {
        return <LoadingState message={t("common.loading")} />;
      }

      if (error) {
        return <ErrorState error={error} />;
      }

      if (!accumulatedRooms || accumulatedRooms.length === 0) {
        return <EmptyState message={t("chat.noChatRoomsFound")} />;
      }

      return (
        <div className="space-y-1">
          {accumulatedRooms.map((room) => {
            const isSelected = currentChatRoomId === String(room.id);
            return (
              <ListItem
                key={room.id}
                icon={
                  <CustomAvatar name={room.title} imageUrl={room.image_url} size={32} roundedFull />
                }
                label={room.title}
                sublabel={room.display_title}
                className={`px-2 ${isSelected ? "bg-variable-collection-main text-white" : ""}`}
                onClick={() => handleChatItemClick(room.id)}
                hasNewEntry={room.has_new_entry}
                selected={isSelected}
              />
            );
          })}

          {hasMore && (
            <LoadMoreButton
              onLoadMore={loadMore}
              isLoading={isLoadingMore}
              loadingText={t("common.loading")}
              moreText={t("common.showMore")}
            />
          )}
        </div>
      );
    };

    const actionButtons = (
      <div className="flex items-center space-x-1">
        <Button
          onClick={() => setIsSearchModalOpen(true)}
          variant="ghost"
          size="icon"
          className="h-6 w-6 rounded-lg p-0.5"
        >
          <Search />
        </Button>
        <Button
          onClick={() => setIsCreateModalOpen(true)}
          variant="ghost"
          size="icon"
          className="h-6 w-6 rounded-lg bg-custom-color-lightTeal p-0.5 text-white"
        >
          <Plus />
        </Button>
      </div>
    );

    return (
      <>
        <ListContainer
          headerProps={{
            title: t("chat.chats"),
            actionButton: actionButtons,
          }}
          containerHeight={containerHeight}
          className="overflow-x-hidden"
        >
          {renderContent()}
        </ListContainer>

        <CreateChatModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onChatCreated={handleChatCreated}
        />

        <ChatSearchModal
          isOpen={isSearchModalOpen}
          onClose={() => setIsSearchModalOpen(false)}
          onChatRoomSelect={handleChatItemClick}
        />
      </>
    );
  },
);
